import api from './api';

export interface Client {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  cnpj: string;
  email: string;
  contatoNome: string | null;
  contatoTelefone: string | null;
  contatoEmail: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  active: boolean;
  createdAt: string;
  _count?: { unidades: number; documents: number };
  unidades?: Unidade[];
}

export interface Unidade {
  id: number;
  nome: string;
  cnpj: string | null;
  contatoNome: string | null;
  contatoTelefone: string | null;
  contatoEmail: string | null;
  cep: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  active: boolean;
  clientId: string;
}

export interface TipoDocumento {
  id: number;
  nome: string;
  descricao: string | null;
  icone: string | null;
  ordem: number;
  active: boolean;
  isPmoc: boolean;
  isNf: boolean;
  isProposta: boolean;
  _count?: { documents: number };
}

export interface ClientListResponse {
  data: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  createdAt: string;
}

export interface UserListResponse {
  data: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ClientUser {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  clientId: string;
  createdAt: string;
  tiposPermitidos?: string; // JSON array: ["pmoc", "nf", "proposta"]
}

export const adminService = {
  // Clientes
  async listClients(search?: string, page = 1): Promise<ClientListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    const response = await api.get<ClientListResponse>(`/admin/clients?${params}`);
    return response.data;
  },

  async getClient(id: string): Promise<Client> {
    const response = await api.get<Client>(`/admin/clients/${id}`);
    return response.data;
  },

  async createClient(data: Partial<Client> & { password: string }): Promise<Client> {
    const response = await api.post<Client>('/admin/clients', data);
    return response.data;
  },

  async updateClient(id: string, data: Partial<Client> & { password?: string }): Promise<Client> {
    const response = await api.put<Client>(`/admin/clients/${id}`, data);
    return response.data;
  },

  async deleteClient(id: string): Promise<void> {
    await api.delete(`/admin/clients/${id}`);
  },

  // Unidades
  async listUnidades(clientId: string): Promise<Unidade[]> {
    const response = await api.get<Unidade[]>(`/admin/clients/${clientId}/unidades`);
    return response.data;
  },

  async createUnidade(clientId: string, data: Partial<Unidade>): Promise<Unidade> {
    const response = await api.post<Unidade>(`/admin/clients/${clientId}/unidades`, data);
    return response.data;
  },

  async updateUnidade(id: number, data: Partial<Unidade>): Promise<Unidade> {
    const response = await api.put<Unidade>(`/admin/unidades/${id}`, data);
    return response.data;
  },

  async deleteUnidade(id: number): Promise<void> {
    await api.delete(`/admin/unidades/${id}`);
  },

  // Tipos de Documento
  async listTiposDocumento(): Promise<TipoDocumento[]> {
    const response = await api.get<TipoDocumento[]>('/admin/tipos-documento');
    return response.data;
  },

  async createTipoDocumento(data: Partial<TipoDocumento>): Promise<TipoDocumento> {
    const response = await api.post<TipoDocumento>('/admin/tipos-documento', data);
    return response.data;
  },

  async updateTipoDocumento(id: number, data: Partial<TipoDocumento>): Promise<TipoDocumento> {
    const response = await api.put<TipoDocumento>(`/admin/tipos-documento/${id}`, data);
    return response.data;
  },

  async deleteTipoDocumento(id: number): Promise<void> {
    await api.delete(`/admin/tipos-documento/${id}`);
  },

  // Upload de documentos
  async uploadDocument(formData: FormData): Promise<{ id: string; nome: string }> {
    const response = await api.post('/admin/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async deleteDocument(id: string): Promise<void> {
    await api.delete(`/admin/documents/${id}`);
  },

  async updateDocument(id: string, data: { nome: string; tipoDocumentoId: number; unidadeId: number | null; dataReferencia: string }): Promise<void> {
    await api.put(`/admin/documents/${id}`, data);
  },

  async listDocumentsByClient(clientId: string, filters?: { 
    page?: number; 
    limit?: number; 
    tipoDocumentoId?: number;
    unidadeId?: number;
    dataInicio?: string;
    dataFim?: string;
  }): Promise<{ data: any[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const params = new URLSearchParams();
    params.append('clientId', clientId);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.tipoDocumentoId) params.append('tipoDocumentoId', filters.tipoDocumentoId.toString());
    if (filters?.unidadeId) params.append('unidade', filters.unidadeId.toString());
    if (filters?.dataInicio) params.append('dataInicio', filters.dataInicio);
    if (filters?.dataFim) params.append('dataFim', filters.dataFim);
    const response = await api.get(`/documents?${params}`);
    return response.data;
  },

  // Usuários
  async listUsers(search?: string, page = 1): Promise<UserListResponse> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    params.append('page', page.toString());
    const response = await api.get<UserListResponse>(`/admin/users?${params}`);
    return response.data;
  },

  async getUser(id: string): Promise<User> {
    const response = await api.get<User>(`/admin/users/${id}`);
    return response.data;
  },

  async createUser(data: Partial<User> & { password: string }): Promise<User> {
    const response = await api.post<User>('/admin/users', data);
    return response.data;
  },

  async updateUser(id: string, data: Partial<User> & { password?: string }): Promise<User> {
    const response = await api.put<User>(`/admin/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete(`/admin/users/${id}`);
  },

  // Usuários de Clientes
  async listClientUsers(clientId: string): Promise<ClientUser[]> {
    const response = await api.get<ClientUser[]>(`/admin/clients/${clientId}/users`);
    return response.data;
  },

  async createClientUser(clientId: string, data: Partial<ClientUser> & { password: string }): Promise<ClientUser> {
    const response = await api.post<ClientUser>(`/admin/clients/${clientId}/users`, data);
    return response.data;
  },

  async updateClientUser(id: string, data: Partial<ClientUser> & { password?: string }): Promise<ClientUser> {
    const response = await api.put<ClientUser>(`/admin/client-users/${id}`, data);
    return response.data;
  },

  async deleteClientUser(id: string): Promise<void> {
    await api.delete(`/admin/client-users/${id}`);
  },
};
